import React, { useState, useEffect, useCallback } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation, Outlet } from "react-router-dom";
import axios from "axios";
import { AppContext } from "./context/AppContext";
import LoginForm from "./components/LoginForm";
import MainSidebar from "./components/MainSidebar";
import About from "./components/About";
// import Classifieds from "./components/matrimonial/Classifieds";
import PageUnderConstruction from "./components/PageUnderConstruction";
import { API_URL } from "./creds";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AddNew from "./components/matrimonial/AddNew";
import View from "./components/matrimonial/View";
import PrintPreview from "./components/matrimonial/PrintPreview";

// Protected route wrapper
const ProtectedRoute = ({ isAuthenticated, children }) => {
  const location = useLocation();
  return isAuthenticated ? (
    children
  ) : (
    <Navigate to="/login" state={{ from: location }} replace />
  );
};

const Login = ({ onLogin }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";

  const handleLogin = useCallback(
    (email, token, refreshToken, expiresIn) => {
      onLogin(email, token, refreshToken, expiresIn);
      navigate(from, { replace: true });
    },
    [onLogin, navigate, from]
  );

  return <LoginForm onLogin={handleLogin} />;
};

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);

  const isTokenExpired = (token) => {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const expiry = payload.exp * 1000;
      return Date.now() >= expiry;
    } catch (err) {
      console.error("isTokenExpired: Token decode failed:", err);
      return true;
    }
  };

  const refreshToken = async () => {
    const storedRefreshToken = localStorage.getItem("refresh_token");
    if (!storedRefreshToken) throw new Error("No refresh token available");
    const response = await axios.post(`${API_URL}/refresh`, {
      refresh_token: storedRefreshToken,
    });
    const {
      access_token,
      refresh_token: newRefreshToken,
      expires_in,
    } = response.data;
    localStorage.setItem("access_token", access_token);
    localStorage.setItem("refresh_token", newRefreshToken);
    localStorage.setItem("expires_at", Date.now() + expires_in * 1000);
    return access_token;
  };

  const verifyToken = async (token) => {
    let currentToken = token;
    if (isTokenExpired(token)) {
      console.log("Token expired, attempting refresh");
      currentToken = await refreshToken();
    }
    const response = await axios.get(`${API_URL}/current_user`, {
      headers: { Authorization: `Bearer ${currentToken}` },
    });
    setIsAuthenticated(true);
    setUserRole(response.data.role);
    setIsAdmin(response.data.role === "admin");
  };

  const handleLogin = useCallback((email, token, refreshToken, expiresIn) => {
    localStorage.setItem("access_token", token);
    localStorage.setItem("refresh_token", refreshToken);
    localStorage.setItem("expires_at", Date.now() + expiresIn * 1000);
    setIsAuthenticated(true);
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      setUserRole(payload.role);
      setIsAdmin(payload.role === "admin");
    } catch (err) {
      console.error("handleLogin: Token decode failed:", err);
    }
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("expires_at");
    setIsAuthenticated(false);
    setUserRole(null);
    setIsAdmin(false);
  }, []);

  const toggleSidebar = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (token) {
      verifyToken(token)
        .catch((err) => {
          console.error("verifyToken failed on reload:", err);
          handleLogout();
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [handleLogout]);

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <AppContext.Provider
      value={{
        isCollapsed,
        toggleSidebar,
        isAuthenticated,
        isAdmin,
        userRole,
        handleLogout,
      }}
    >
      <Router>
        <ToastContainer
          position="top-center"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="colored"
        />
        <div className="app-container">
          <Routes>
            <Route
              element={
                <MainSidebar onLogout={handleLogout} isCollapsed={isCollapsed}>
                  <Outlet />
                </MainSidebar>
              }
            >
              <Route
                path="/"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <View sidebarOpen={isCollapsed} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/view"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <View sidebarOpen={isCollapsed} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/addnew"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <AddNew />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/prepare"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <PrintPreview />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/about"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <About />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/real-estate"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <View />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/real-estate/view"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <View />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/real-estate/addnew"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <AddNew />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/real-estate/prepare"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <PrintPreview />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/wanted"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <View />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/wanted/view"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <View />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/wanted/addnew"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <AddNew />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/wanted/prepare"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <PrintPreview />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/others"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <View />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/others/view"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <View />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/others/addnew"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <AddNew />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/others/prepare"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <PrintPreview />
                  </ProtectedRoute>
                }
              />
            </Route>
            <Route path="/login" element={<Login onLogin={handleLogin} />} />
            <Route path="/register" element={<PageUnderConstruction />} />
            <Route
              path="/forgot-password"
              element={<PageUnderConstruction />}
            />
            <Route
              path="*"
              element={
                <Navigate to={isAuthenticated ? "/" : "/login"} replace />
              }
            />
          </Routes>
        </div>
      </Router>
    </AppContext.Provider>
  );
};

export default App;
